import { useState } from 'react'
import { ChoiceGroup, Field, Input, IntegerInput, Select } from '@/ui'
import { useVillages } from '../api/reference'
import {
  LEVEL_LABEL,
  LOCATION_KIND_LABEL,
  LOCATION_KINDS,
  LOCATION_ROLE_LABEL,
  LOCATION_ROLES,
  useSaveLocation,
  type LocationRow,
} from '../api/store'
import { childLevels, suggestChildCode } from '../model/locationTree'
import {
  emptyLocationForm,
  normalizeLocationCode,
  toCreateBody,
  toUpdateBody,
  validateLocationForm,
  type LocationForm,
} from '../model/locationForm'
import { FormDialog } from './FormDialog'

export type LocationDialogMode =
  | { kind: 'site' }
  | { kind: 'child'; parent: LocationRow }
  | { kind: 'edit'; row: LocationRow }

const ROLE_OPTIONS = LOCATION_ROLES.map((r) => ({ value: r, label: LOCATION_ROLE_LABEL[r]!.en }))

function initialForm(mode: LocationDialogMode): LocationForm {
  if (mode.kind === 'site') return emptyLocationForm()
  if (mode.kind === 'edit')
    return emptyLocationForm({
      level: mode.row.level,
      kind: mode.row.kind ?? '',
      code: mode.row.code,
      name: mode.row.name,
      role: mode.row.role ?? '',
      capacityKg: mode.row.capacityKg,
      fssaiLicenceNo: mode.row.fssaiLicenceNo ?? '',
      fssaiLicenceValidTo: mode.row.fssaiLicenceValidTo ?? '',
      active: mode.row.active,
    })
  const level = childLevels(mode.parent.level)[0] ?? 'BAY'
  return emptyLocationForm({
    level,
    kind: mode.parent.kind ?? '',
    code: suggestChildCode(mode.parent.code, level, (mode.parent.children ?? []).map((c) => c.code)),
  })
}

/** New site, new place under a parent, or edit a place. Mount with a `key` per opening. */
export function LocationFormDialog({
  mode,
  onClose,
  onSaved,
}: {
  mode: LocationDialogMode
  onClose: () => void
  onSaved?: (id: string) => void
}) {
  const save = useSaveLocation()
  const villages = useVillages()
  const [f, setF] = useState<LocationForm>(() => initialForm(mode))
  const [touched, setTouched] = useState(false)
  const set = (patch: Partial<LocationForm>) => setF((p) => ({ ...p, ...patch }))
  const edit = mode.kind === 'edit'
  const isSite = f.level === 'SITE'
  const errors = validateLocationForm(f, edit ? 'edit' : 'create')
  const err = (k: keyof LocationForm) => (touched ? errors[k] : undefined)
  const levels = mode.kind === 'child' ? childLevels(mode.parent.level) : []

  const title =
    mode.kind === 'site' ? 'New site' : mode.kind === 'child' ? `Add a place under ${mode.parent.name}` : `Edit ${mode.row.name}`

  const onSave = async () => {
    setTouched(true)
    if (Object.keys(errors).length > 0) throw new Error('invalid')
    const res =
      mode.kind === 'edit'
        ? await save.mutateAsync({ id: mode.row.id, body: toUpdateBody(f, isSite, mode.row) })
        : await save.mutateAsync({ body: toCreateBody(f, mode.kind === 'child' ? mode.parent.id : null) })
    const id = typeof res?.id === 'string' ? res.id : mode.kind === 'edit' ? mode.row.id : null
    if (id) onSaved?.(id)
  }

  return (
    <FormDialog
      open
      onClose={onClose}
      title={title}
      description={
        mode.kind === 'site'
          ? 'A warehouse, collection point or unit. A label is made for it from the code.'
          : mode.kind === 'child'
            ? 'A label is made for the new place from its code; print it and stick it up before first use.'
            : undefined
      }
      saveLabel={edit ? 'Save' : 'Create'}
      loading={save.isPending}
      onSave={onSave}
    >
      {mode.kind === 'site' && (
        <Field label="Kind" required error={err('kind')}>
          <Select
            value={f.kind}
            onChange={(kind) => set({ kind, groupId: kind === 'COLLECTION_POINT' ? f.groupId : '' })}
            placeholder="Choose…"
            options={LOCATION_KINDS.map((k) => ({ value: k, label: LOCATION_KIND_LABEL[k]! }))}
          />
        </Field>
      )}
      {levels.length > 1 && (
        <Field label="Level" required>
          <ChoiceGroup
            value={f.level}
            onChange={(level) =>
              mode.kind === 'child' &&
              set({ level, code: suggestChildCode(mode.parent.code, level, (mode.parent.children ?? []).map((c) => c.code)) })
            }
            options={levels.map((l) => ({ value: l, label: LEVEL_LABEL[l] ?? l }))}
          />
        </Field>
      )}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field
          label="Code"
          required={!edit}
          error={err('code')}
          hint={edit ? 'Codes never change' : f.code && normalizeLocationCode(f.code) !== f.code ? `Saved as ${normalizeLocationCode(f.code)}` : 'Printed under the label'}
        >
          <Input value={f.code} onChange={(e) => set({ code: e.target.value })} disabled={edit} className="font-mono" maxLength={40} data-autofocus={!edit || undefined} />
        </Field>
        <Field label="Name" required error={err('name')}>
          <Input value={f.name} onChange={(e) => set({ name: e.target.value })} maxLength={90} placeholder={isSite ? 'Godown A' : `${LEVEL_LABEL[f.level] ?? ''} 1`} data-autofocus={edit || undefined} />
        </Field>
      </div>
      {!isSite && (
        <Field label="Used for" hint={edit && f.role ? undefined : 'Leave empty for ordinary storage'}>
          <Select value={f.role} onChange={(role) => set({ role })} placeholder="Storage" options={ROLE_OPTIONS} />
        </Field>
      )}
      <Field label="Capacity" error={err('capacityKg')} hint="Whole kg">
        <IntegerInput value={f.capacityKg} onChange={(capacityKg) => set({ capacityKg })} maxDigits={9} trailing={<span className="font-mono">kg</span>} />
      </Field>
      {mode.kind === 'site' && f.kind === 'COLLECTION_POINT' && (
        <Field label="Village">
          <Select
            value={f.groupId}
            onChange={(groupId) => set({ groupId })}
            placeholder="Choose…"
            options={(villages.data ?? []).map((v) => ({ value: v.id, label: v.name }))}
          />
        </Field>
      )}
      {isSite && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="FSSAI licence no." error={err('fssaiLicenceNo')} hint={edit ? 'Empty it to remove the licence' : undefined}>
            <Input value={f.fssaiLicenceNo} onChange={(e) => set({ fssaiLicenceNo: e.target.value })} className="font-mono" maxLength={24} />
          </Field>
          <Field label="Licence valid to" error={err('fssaiLicenceValidTo')}>
            <Input type="date" value={f.fssaiLicenceValidTo} onChange={(e) => set({ fssaiLicenceValidTo: e.target.value })} />
          </Field>
        </div>
      )}
      {edit && (
        <Field label="Status" hint={f.active ? undefined : 'Refused while anything is in it'}>
          <ChoiceGroup
            value={f.active ? 'on' : 'off'}
            onChange={(v) => set({ active: v === 'on' })}
            options={[
              { value: 'on', label: 'In use' },
              { value: 'off', label: 'Not in use', tone: 'warn' },
            ]}
          />
        </Field>
      )}
    </FormDialog>
  )
}
